<template>
  <div>
    <h2>Composition d’un alliage: Calculations pour le métal connu</h2>
    <v-form>
      <!-- Data Table -->
      <h3 class="mb-4 mt-8">Tableau de données</h3>

      <v-textarea
        v-model="inputs.data_table_caption"
        label="Légende"
        outlined
        rows="1"
        class="mb-n3"
        placeholder="Rédigez une légende pour le tableau des constantes ici."
      />

      <v-simple-table>
        <thead>
          <tr>
            <td>&nbsp;</td>
            <td style="text-align: center; font-weight: bold">Unités</td>
            <td style="text-align: center; font-weight: bold">Essai 1</td>
            <td style="text-align: center; font-weight: bold">Essai 2</td>
          </tr>
        </thead>
        <tr>
          <td style="font-weight: bold">Masse</td>
          <td>
            <v-select
              v-model="inputs['mass_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>

          <td v-for="i in 2" :key="`known-${i}`">
            <calculation-input v-model="inputs[`trial${i}_mass`]" class="centered-input" />
          </td>
        </tr>

        <tr>
          <td><stemble-latex content="$p_i$" /></td>
          <td>
            <v-select
              v-model="inputs['initialPressure_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>

          <td v-for="i in 2" :key="`known-${i}`">
            <calculation-input
              v-model="inputs[`trial${i}_initialPressure`]"
              class="centered-input"
            />
          </td>
        </tr>

        <tr>
          <td><stemble-latex content="$p_f$" /></td>
          <td>
            <v-select
              v-model="inputs['finalPressure_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>

          <td v-for="i in 2" :key="`known-${i}`">
            <calculation-input v-model="inputs[`trial${i}_finalPressure`]" class="centered-input" />
          </td>
        </tr>

        <tr>
          <td style="font-weight: bold">Température ambiante</td>
          <td>
            <v-select
              v-model="inputs['roomTemperature_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>

          <td v-for="i in 2" :key="`known-${i}`">
            <calculation-input
              v-model="inputs[`trial${i}_roomTemperature`]"
              class="centered-input"
            />
          </td>
        </tr>

        <tr>
          <td style="font-weight: bold">Masse du flacon vide</td>
          <td>
            <v-select
              v-model="inputs['massEmptyFlask_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>

          <td v-for="i in 2" :key="`known-${i}`">
            <calculation-input
              v-model="inputs[`trial${i}_massEmptyFlask`]"
              class="centered-input"
            />
          </td>
        </tr>

        <tr>
          <td style="font-weight: bold">Masse du flacon rempli</td>
          <td>
            <v-select
              v-model="inputs['massFilledFlask_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>

          <td v-for="i in 2" :key="`known-${i}`">
            <calculation-input
              v-model="inputs[`trial${i}_massFilledFlask`]"
              class="centered-input"
            />
          </td>
        </tr>
      </v-simple-table>

      <!-- Known Metal Calculations Table -->
      <h3 class="mb-4 mt-8">Calculations: Métal connu</h3>
      <v-textarea
        v-model="inputs.calculations_table_caption"
        label="Légende"
        outlined
        rows="1"
        class="mb-n3"
        placeholder="Rédigez une légende pour le tableau des valeurs calculées ici"
      />
      <v-simple-table>
        <thead>
          <tr>
            <td>&nbsp;</td>
            <td style="text-align: center; font-weight: bold">Unités</td>
            <td style="text-align: center; font-weight: bold">Essai 1</td>
            <td style="text-align: center; font-weight: bold">Essai 2</td>
          </tr>
        </thead>
        <tr>
          <td style="font-weight: bold">Quantité de matière du Mg</td>
          <td>
            <v-select
              v-model="inputs['molesOfMetal_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>

          <td v-for="i in 2" :key="`known-${i}`">
            <calculation-input v-model="inputs[`trial${i}_molesOfMetal`]" class="centered-input" />
          </td>
        </tr>

        <tr>
          <td style="font-weight: bold">Quantité de matière théorique de l’hydrogène</td>
          <td>
            <v-select
              v-model="inputs['molesOfHydrogenGasExpected_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>

          <td v-for="i in 2" :key="`known-${i}`">
            <calculation-input
              v-model="inputs[`trial${i}_molesOfHydrogenGasExpected`]"
              class="centered-input"
            />
          </td>
        </tr>

        <tr>
          <td style="font-weight: bold">Volume du flacon</td>
          <td>
            <v-select
              v-model="inputs['volumeOfFlask_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>

          <td v-for="i in 2" :key="`known-${i}`">
            <calculation-input v-model="inputs[`trial${i}_volumeOfFlask`]" class="centered-input" />
          </td>
        </tr>

        <tr>
          <td style="font-weight: bold">Volume du flacon disponible</td>
          <td>
            <v-select
              v-model="inputs['volumeAvailable_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>

          <td v-for="i in 2" :key="`known-${i}`">
            <calculation-input
              v-model="inputs[`trial${i}_volumeAvailable`]"
              class="centered-input"
            />
          </td>
        </tr>

        <tr>
          <td><stemble-latex content="$\Delta p$" /></td>
          <td>
            <v-select
              v-model="inputs['changeInPressure_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>

          <td v-for="i in 2" :key="`known-${i}`">
            <calculation-input
              v-model="inputs[`trial${i}_changeInPressure`]"
              class="centered-input"
            />
          </td>
        </tr>

        <tr>
          <td style="font-weight: bold">Quantité de matière expérimentale de l’hydrogène</td>
          <td>
            <v-select
              v-model="inputs['molesOfHydrogenGasActual_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>

          <td v-for="i in 2" :key="`known-${i}`">
            <calculation-input
              v-model="inputs[`trial${i}_molesOfHydrogenGasActual`]"
              class="centered-input"
            />
          </td>
        </tr>

        <tr>
          <td style="font-weight: bold">Rendement</td>
          <td>
            <v-select
              v-model="inputs['percentYield_units']"
              :items="units"
              item-value="key"
              outlined
              hide-details
            >
              <template #item="{item}">
                <stemble-latex :content="item.label" />
              </template>
              <template #selection="{item}">
                <stemble-latex class="no-text-transform" :content="item.label" />
              </template>
            </v-select>
          </td>

          <td v-for="i in 2" :key="`known-${i}`">
            <calculation-input v-model="inputs[`trial${i}_percentYield`]" class="centered-input" />
          </td>
        </tr>
      </v-simple-table>

      <p class="mt-5">
        Dans l’espace ci-dessous, veuillez écrire vos calculs d’exemple pour cette partie de
        l’expérience. Assurez-vous de montrer toutes vos démarches.
      </p>

      <v-textarea v-model="inputs.sampleCalc" outlined rows="6" auto-grow />
    </v-form>
  </div>
</template>

<script>
import DynamicQuestionMixin from '../../mixins/dynamic-question';
import CalculationInput from '@/tasks/components/inputs/CalculationInput.vue';
import StembleLatex from '@/tasks/components/StembleLatex.vue';

export default {
  name: 'OttawaLab1ReportTask2DataFr',
  components: {StembleLatex, CalculationInput},
  mixins: [DynamicQuestionMixin()],
  data() {
    return {
      inputs: {
        data_table_caption: null,
        constants_table_caption: null,
        calculations_table_caption: null,
        mass_units: null,
        initialPressure_units: null,
        finalPressure_units: null,
        roomTemperature_units: null,
        massEmptyFlask_units: null,
        massFilledFlask_units: null,

        molesOfMetal_units: null,
        molesOfHydrogenGasExpected_units: null,
        volumeOfFlask_units: null,
        volumeAvailable_units: null,
        changeInPressure_units: null,
        molesOfHydrogenGasActual_units: null,
        percentYield_units: null,

        trial1_mass: null,
        trial1_initialPressure: null,
        trial1_finalPressure: null,
        trial1_roomTemperature: null,
        trial1_massEmptyFlask: null,
        trial1_massFilledFlask: null,
        trial2_mass: null,
        trial2_initialPressure: null,
        trial2_finalPressure: null,
        trial2_roomTemperature: null,
        trial2_massEmptyFlask: null,
        trial2_massFilledFlask: null,

        trial1_molesOfMetal: null,
        trial1_molesOfHydrogenGasExpected: null,
        trial1_volumeOfFlask: null,
        trial1_volumeAvailable: null,
        trial1_changeInPressure: null,
        trial1_molesOfHydrogenGasActual: null,
        trial1_percentYield: null,
        trial2_molesOfMetal: null,
        trial2_molesOfHydrogenGasExpected: null,
        trial2_volumeOfFlask: null,
        trial2_volumeAvailable: null,
        trial2_changeInPressure: null,
        trial2_molesOfHydrogenGasActual: null,
        trial2_percentYield: null,

        sampleCalc: null,
      },
      units: [
        {key: 'g', label: '$\\text{g}$'},
        {key: 'kPa', label: '$\\text{kPa}$'},
        {key: 'mL', label: '$\\text{mL}$'},
        {key: 'gPerMol', label: '$\\text{g} \\cdot \\text{mol}^{-1}$'},
        {key: 'degreesCelsius', label: '$^\\omicron\\text{C}$'},
        {key: 'gramsPerMillilitre', label: '$\\text{g} \\cdot \\text{mL}^{-1}$'},
        {
          key: 'joulesPerKelvinPerMole',
          label: '$\\text{J} \\cdot \\text{K}^{-1} \\cdot \\text{mol}^{-1}$',
        },
        {
          key: 'moles',
          label: '$\\text{mol}$',
        },
        {
          key: 'percent',
          label: '$\\text{\\%}$',
        },
      ],
    };
  },
};
</script>
